// features/SearchReplace/index.js - Search and Replace feature
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAppContext } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import UploadCard from '../../components/UploadCard';
import ImagePreview from '../../components/ImagePreview';
import { FaMagic, FaDownload, FaRedo, FaSearch, FaExchangeAlt, FaInfoCircle, FaBan, FaPalette } from 'react-icons/fa';
import apiService from '../../services/api';
import { Spinner } from '../../components/Spinner';
import { useTranslation } from 'react-i18next';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

function SearchReplace() {
  const { setIsLoading } = useAppContext();
  const { t } = useTranslation();
  const [searchPrompt, setSearchPrompt] = useState('');
  const [replacePrompt, setReplacePrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [stylePreset, setStylePreset] = useState('');
  const [image, setImage] = useState(null);
  const [resultImage, setResultImage] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // Debug current state
  useEffect(() => {
    console.log('Current state:', { 
      image, 
      searchPrompt, 
      replacePrompt, 
      isFormValid 
    });
  }, [image, searchPrompt, replacePrompt, isFormValid]);

  // Update form validity when dependencies change
  useEffect(() => {
    const valid = image !== null && searchPrompt.trim() !== '' && replacePrompt.trim() !== '';
    console.log('Form validity updated:', valid, { 
      image: image !== null, 
      searchPrompt: searchPrompt.trim() !== '', 
      replacePrompt: replacePrompt.trim() !== '' 
    });
    setIsFormValid(valid);
  }, [image, searchPrompt, replacePrompt]);

  // Handle image upload
  const handleImageUpload = (file) => {
    console.log('File received in handleImageUpload:', file);
    
    if (!file) {
      console.error('No file received in handleImageUpload');
      return;
    }
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      console.error('Invalid file type:', file.type);
      alert(t('errors.invalidFileType'));
      return;
    }
    
    // Validate file size
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      console.error('File too large:', file.size);
      alert(t('errors.fileSizeTooLarge'));
      return;
    }
    
    console.log('File passed validation, creating FileReader');
    
    // Create a new FileReader instance
    const reader = new FileReader();
    
    // Set up the onload callback
    reader.onload = (e) => {
      try {
        console.log('FileReader onload triggered');
        
        // Create the image object with the file and data URL
        const imageData = {
          file: file,
          src: e.target.result
        };
        
        console.log('Image data created:', {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          dataUrlLength: e.target.result.length
        });
        
        // Update the state with the new image
        console.log('Setting image state...');
        setImage(imageData);
        
        // Force a re-render to ensure the state update is reflected
        setTimeout(() => {
          console.log('Current image state after timeout:', image);
        }, 100);
      } catch (error) {
        console.error('Error in FileReader onload:', error);
        alert(t('errors.fileReadError'));
      }
    };
    
    // Set up error handling
    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      alert(t('errors.fileReadError'));
    };
    
    // Start reading the file
    console.log('Starting to read file as data URL');
    try {
      reader.readAsDataURL(file);
      console.log('ReadAsDataURL called successfully');
    } catch (error) {
      console.error('Error calling readAsDataURL:', error);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) {
      console.log('Form is not valid, cannot submit', { 
        image: image !== null, 
        searchPrompt: searchPrompt.trim() !== '', 
        replacePrompt: replacePrompt.trim() !== '' 
      });
      return;
    }

    setIsLoading(true);
    setIsProcessing(true);
    try {
      // Call the API
      const params = {
        searchPrompt,
        replacePrompt,
        negativePrompt,
        stylePreset
      };
      
      const result = await apiService.searchAndReplace(image.file, params);
      
      if (result.success) {
        // Convert the output path to a full URL if it's a relative path
        let resultUrl = apiService.getFullUrl(result.outputPath);
        
        setResultImage(resultUrl);
        setShowResult(true);
      } else {
        throw new Error(result.message || t('errors.processingFailed'));
      }
    } catch (error) {
      console.error('Error processing image:', error);
      alert(t('errors.processingFailed'));
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  };

  // Reset the form
  const handleReset = () => {
    setImage(null);
    setSearchPrompt('');
    setReplacePrompt('');
    setNegativePrompt('');
    setStylePreset('');
    setResultImage(null);
    setShowResult(false);
  };

  // Download the result
  const handleDownload = () => {
    if (!resultImage) return;
    
    const link = document.createElement('a');
    link.href = resultImage;
    link.download = 'replaced-image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div 
      className="container mx-auto px-4 py-8 max-w-6xl"
      initial="hidden"
      animate="visible"
      variants={{
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
      }}
    >
      <motion.div 
        className="mb-8 flex items-center justify-between"
        variants={fadeIn}
      >
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-primary/80 to-primary p-3 rounded-xl shadow-md">
            <FaExchangeAlt className="text-white text-xl" />
          </div>
          <h2 className="text-2xl font-bold text-primary">{t('features.searchReplace.title')}</h2>
        </div>
      </motion.div>
      
      <div className="content-area">
        {!showResult ? (
          <>
            <motion.div variants={fadeIn} className="mb-6">
              <Card variant="glass" className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <FaInfoCircle className="text-primary mt-1 flex-shrink-0 text-lg" />
                    <div>
                      <p className="font-medium">{t('common.howItWorks')}</p>
                      <p className="text-text-light text-sm mt-1">
                        {t('features.searchReplace.description')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            
            <motion.form 
              onSubmit={handleSubmit} 
              className="space-y-6"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
              }}
            >
              {!image && (
                <motion.div variants={fadeIn} className="mb-6">
                  <UploadCard
                    title={t('common.uploadImage')}
                    description={t('common.uploadImageDescription')}
                    onFileAccepted={handleImageUpload}
                    acceptedFileTypes="image/jpeg,image/png,image/webp"
                  />
                </motion.div>
              )}
              
              {image && (
                <motion.div 
                  variants={fadeIn}
                  className="bg-card/30 backdrop-blur-sm p-6 rounded-xl shadow-sm border border-border/50"
                >
                  <h3 className="text-lg font-medium mb-4 text-primary">{t('common.uploadedImage')}</h3>
                  <div className="flex flex-col md:flex-row gap-4 items-center">
                    <div className="w-full md:w-2/3">
                      <ImagePreview
                        src={image.src}
                        alt={t('common.uploadedImage')}
                        className="w-full"
                      />
                    </div>
                    <div className="w-full md:w-1/3 flex justify-center">
                      <Button
                        variant="outline"
                        onClick={() => setImage(null)}
                        size="sm"
                        className="mt-2"
                      >
                        {t('common.changeImage')}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
              
              <motion.div 
                variants={fadeIn}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <Card variant="glass" className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4 text-primary">
                      <FaSearch className="text-lg" />
                      <label className="text-lg font-medium" htmlFor="search-prompt">
                        {t('searchReplace.whatToFind')}
                      </label>
                    </div>
                    <input
                      type="text"
                      id="search-prompt"
                      className="w-full p-3 rounded-lg border border-background/50 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
                      value={searchPrompt}
                      onChange={(e) => setSearchPrompt(e.target.value)}
                      placeholder={t('searchReplace.findPlaceholder')}
                      required
                    />
                    <p className="text-xs text-text-light mt-2">{t('searchReplace.findDescription')}</p>
                  </CardContent>
                </Card>
                
                <Card variant="glass" className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4 text-primary">
                      <FaExchangeAlt className="text-lg" />
                      <label className="text-lg font-medium" htmlFor="replace-prompt">
                        {t('searchReplace.replaceWith')}
                      </label>
                    </div>
                    <textarea
                      id="replace-prompt"
                      className="w-full p-3 rounded-lg border border-background/50 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all min-h-[100px] resize-y"
                      value={replacePrompt}
                      onChange={(e) => setReplacePrompt(e.target.value)}
                      placeholder={t('searchReplace.replacePlaceholder')}
                      required
                    />
                  </CardContent>
                </Card>
              </motion.div>
              
              <motion.div 
                variants={fadeIn}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <Card variant="glass" className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4 text-primary">
                      <FaBan className="text-lg" />
                      <label className="text-lg font-medium" htmlFor="negative-prompt">
                        {t('common.negativePrompt')}
                      </label>
                    </div>
                    <textarea
                      id="negative-prompt"
                      className="w-full p-3 rounded-lg border border-background/50 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all min-h-[80px] resize-y"
                      value={negativePrompt}
                      onChange={(e) => setNegativePrompt(e.target.value)}
                      placeholder={t('common.negativePromptPlaceholder')}
                    />
                  </CardContent>
                </Card>
                
                <Card variant="glass" className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4 text-primary">
                      <FaPalette className="text-lg" />
                      <label className="text-lg font-medium" htmlFor="style-preset">
                        {t('common.stylePreset')}
                      </label>
                    </div>
                    <select
                      id="style-preset"
                      className="w-full p-3 rounded-lg border border-background/50 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
                      value={stylePreset}
                      onChange={(e) => setStylePreset(e.target.value)}
                    >
                      <option value="">{t('common.noPreset')}</option>
                      <option value="photographic">{t('common.stylePresets.photographic')}</option>
                      <option value="digital-art">{t('common.stylePresets.digitalArt')}</option>
                      <option value="cinematic">{t('common.stylePresets.cinematic')}</option>
                      <option value="3d-model">{t('common.stylePresets.3dModel')}</option>
                      <option value="anime">{t('common.stylePresets.anime')}</option>
                      <option value="fantasy-art">{t('common.stylePresets.fantasyArt')}</option>
                      <option value="pixel-art">{t('common.stylePresets.pixelArt')}</option>
                      <option value="isometric">{t('common.stylePresets.isometric')}</option>
                      <option value="low-poly">{t('common.stylePresets.lowPoly')}</option>
                      <option value="origami">{t('common.stylePresets.origami')}</option>
                      <option value="line-art">{t('common.stylePresets.lineArt')}</option>
                      <option value="sketch">{t('common.stylePresets.sketch')}</option>
                      <option value="watercolor">{t('common.stylePresets.watercolor')}</option>
                      <option value="oil-painting">{t('common.stylePresets.oilPainting')}</option>
                    </select>
                  </CardContent>
                </Card>
              </motion.div>
              
              <motion.div 
                variants={fadeIn}
                className="flex justify-center mt-8"
              >
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Button
                    type="submit"
                    disabled={!isFormValid || isProcessing}
                    className="px-8 py-6 text-lg"
                  >
                    {isProcessing ? <Spinner size="sm" className="mr-2" /> : <FaMagic className="mr-2" />}
                    {isProcessing ? t('common.processing') : t('searchReplace.generateReplacement')}
                  </Button>
                </motion.div>
              </motion.div>
            </motion.form>
          </>
        ) : (
          <motion.div 
            className="result-container"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div 
              className="flex items-center justify-between mb-6"
              variants={fadeIn}
            >
              <h2 className="text-2xl font-bold text-primary">{t('common.replacementResult')}</h2>
              <Button variant="ghost" onClick={handleReset} size="sm">
                <FaRedo className="mr-2" />
                {t('common.startOver')}
              </Button>
            </motion.div>
            
            <motion.div 
              className="grid grid-cols-1 lg:grid-cols-2 gap-8"
              variants={fadeIn}
            >
              <Card variant="glass" className="overflow-hidden">
                <CardContent className="p-0">
                  {resultImage ? (
                    <img src={resultImage} alt={t('common.processedImage')} className="w-full h-auto" />
                  ) : (
                    <div className="flex items-center justify-center h-64 bg-background/50">
                      <p className="text-text-light">{t('common.processingFailed')}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card variant="glass" className="overflow-hidden">
                <CardContent className="p-6">
                  <div className="mb-6">
                    <h3 className="text-xl font-medium text-primary mb-2">{t('common.objectReplaced')}</h3>
                    <p className="text-text-light">
                      {t('features.searchReplace.replacementSuccess', { searchPrompt })}
                    </p>
                  </div>
                  
                  <div className="flex flex-wrap gap-3 mb-8">
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        onClick={handleDownload}
                        disabled={!resultImage}
                        className="gap-2"
                      >
                        <FaDownload />
                        {t('common.download')}
                      </Button>
                    </motion.div>
                    
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="outline"
                        onClick={handleReset}
                        className="gap-2"
                      >
                        <FaRedo />
                        {t('common.createAnother')}
                      </Button>
                    </motion.div>
                  </div>
                  
                  <div className="bg-background/30 backdrop-blur-sm p-4 rounded-lg">
                    <h4 className="font-medium mb-3 text-primary">{t('common.generationDetails')}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex flex-col">
                        <span className="text-text-light">{t('searchReplace.whatToFind')}:</span>
                        <span className="font-medium">"{searchPrompt}"</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-text-light">{t('searchReplace.replaceWith')}:</span>
                        <span className="font-medium">"{replacePrompt}"</span>
                      </div>
                      {negativePrompt && (
                        <div className="flex flex-col">
                          <span className="text-text-light">{t('common.negativePrompt')}:</span>
                          <span className="font-medium">"{negativePrompt}"</span>
                        </div>
                      )}
                      {stylePreset && (
                        <div className="flex flex-col">
                          <span className="text-text-light">{t('common.stylePreset')}:</span>
                          <span className="font-medium">{stylePreset}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            
            <motion.div 
              className="mt-10"
              variants={fadeIn}
            >
              <h3 className="text-xl font-medium text-primary mb-4">{t('common.beforeAfterComparison')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <ImagePreview 
                    src={image.src} 
                    alt={t('common.originalImage')} 
                    label={t('common.before')}
                  />
                </div>
                <div className="relative">
                  <ImagePreview 
                    src={resultImage} 
                    alt={t('common.processedImage')} 
                    label={t('common.after')}
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default SearchReplace;
